/**
 * Batch Title Settings Page
 * Allows manufacturers to manage product/unit/process codes used in batch titles.
 */

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import {
  productService,
  BatchTitleSettings,
  BatchTitleSettingsCode,
  BatchTitleCodeType
} from '../services/product.service';
import { Alert, Button, FilterBar, Input, PageHeader, StatusBadge } from '../components/ui';

type SettingsKey = 'productCodes' | 'unitCodes' | 'processVersions';

const settingsKeyForType = (type: BatchTitleCodeType): SettingsKey => {
  if (type === 'product') return 'productCodes';
  if (type === 'unit') return 'unitCodes';
  return 'processVersions';
};

const sortByCode = (items: BatchTitleSettingsCode[]) =>
  [...items].sort((a, b) => a.code.localeCompare(b.code));

const unitTypeHelpText =
  'Unit Type means the category of unit where the batch is produced. Example: kitchen, farm, line, plant, warehouse.';

interface BatchTitleSettingsPageProps {
  embedded?: boolean;
  onSettingsChanged?: () => void | Promise<void>;
}

export function BatchTitleSettingsManager({
  embedded = false,
  onSettingsChanged,
}: BatchTitleSettingsPageProps) {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<BatchTitleSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [creatingType, setCreatingType] = useState<BatchTitleCodeType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<'code-asc' | 'code-desc'>('code-asc');

  const [newProductCode, setNewProductCode] = useState('');
  const [newProductName, setNewProductName] = useState('');
  const [newUnitCode, setNewUnitCode] = useState('');
  const [newUnitName, setNewUnitName] = useState('');
  const [newUnitType, setNewUnitType] = useState('');
  const [newProcessCode, setNewProcessCode] = useState('');
  const [newProcessName, setNewProcessName] = useState('');

  const syncParentSettings = async () => {
    if (!onSettingsChanged) {
      return;
    }

    try {
      await onSettingsChanged();
    } catch (syncError) {
      console.error('Failed to sync batch title options:', syncError);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productService.getBatchTitleSettings();
      setSettings({
        productCodes: sortByCode(data.productCodes),
        unitCodes: sortByCode(data.unitCodes),
        processVersions: sortByCode(data.processVersions)
      });
    } catch (err: any) {
      console.error('Failed to load batch title settings:', err);
      setError(err.response?.data?.error || 'Failed to load batch title settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const updateLocal = (
    type: BatchTitleCodeType,
    id: string,
    patch: Partial<BatchTitleSettingsCode>
  ) => {
    setSettings((prev) => {
      if (!prev) return prev;
      const key = settingsKeyForType(type);
      const updated = prev[key].map((item) => (item.id === id ? { ...item, ...patch } : item));
      return { ...prev, [key]: updated };
    });
  };

  const applyUpdatedRecord = (type: BatchTitleCodeType, record: BatchTitleSettingsCode) => {
    setSettings((prev) => {
      if (!prev) return prev;
      const key = settingsKeyForType(type);
      const existingIndex = prev[key].findIndex((item) => item.id === record.id);
      const nextList = existingIndex >= 0
        ? prev[key].map((item) => (item.id === record.id ? record : item))
        : [...prev[key], record];
      return { ...prev, [key]: sortByCode(nextList) };
    });
  };

  const handleCreate = async (type: BatchTitleCodeType) => {
    setActionError(null);
    setActionSuccess(null);
    setCreatingType(type);

    try {
      if (type === 'product') {
        if (!newProductCode.trim()) {
          setActionError('Product code is required.');
          return;
        }
        const record = await productService.createBatchTitleCode(type, {
          code: newProductCode.trim(),
          name: newProductName.trim() || undefined
        });
        applyUpdatedRecord(type, record);
        setNewProductCode('');
        setNewProductName('');
        setActionSuccess('Product code saved.');
      }
      if (type === 'unit') {
        if (!newUnitCode.trim()) {
          setActionError('Unit code is required.');
          return;
        }
        const record = await productService.createBatchTitleCode(type, {
          code: newUnitCode.trim(),
          name: newUnitName.trim() || undefined,
          unitType: newUnitType.trim() || undefined
        });
        applyUpdatedRecord(type, record);
        setNewUnitCode('');
        setNewUnitName('');
        setNewUnitType('');
        setActionSuccess('Unit code saved.');
      }
      if (type === 'process') {
        if (!newProcessCode.trim()) {
          setActionError('Process version code is required.');
          return;
        }
        const record = await productService.createBatchTitleCode(type, {
          code: newProcessCode.trim(),
          name: newProcessName.trim() || undefined
        });
        applyUpdatedRecord(type, record);
        setNewProcessCode('');
        setNewProcessName('');
        setActionSuccess('Process version saved.');
      }

      await syncParentSettings();
    } catch (err: any) {
      console.error('Failed to create code:', err);
      setActionError(err.response?.data?.error || 'Failed to save code.');
    } finally {
      setCreatingType(null);
    }
  };

  const handleSave = async (type: BatchTitleCodeType, item: BatchTitleSettingsCode) => {
    setActionError(null);
    setActionSuccess(null);
    setSavingId(item.id);

    try {
      const record = await productService.updateBatchTitleCode(type, item.id, {
        name: item.name ?? '',
        unitType: type === 'unit' ? item.unitType ?? '' : undefined,
        isActive: item.isActive
      });
      applyUpdatedRecord(type, record);
      setActionSuccess('Changes saved.');
      await syncParentSettings();
    } catch (err: any) {
      console.error('Failed to update code:', err);
      setActionError(err.response?.data?.error || 'Failed to update code.');
    } finally {
      setSavingId(null);
    }
  };

  const allCodes = useMemo(
    () =>
      settings
        ? [...settings.productCodes, ...settings.unitCodes, ...settings.processVersions]
        : [],
    [settings]
  );
  const activeCodes = allCodes.filter((item) => item.isActive).length;
  const inactiveCodes = allCodes.length - activeCodes;

  const filterAndSortCodes = (items: BatchTitleSettingsCode[]) => {
    const query = searchTerm.trim().toLowerCase();

    return items
      .filter((item) => {
        const matchesSearch =
          !query ||
          item.code.toLowerCase().includes(query) ||
          (item.name || '').toLowerCase().includes(query) ||
          (item.unitType || '').toLowerCase().includes(query);
        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' ? item.isActive : !item.isActive);
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) =>
        sortBy === 'code-asc'
          ? a.code.localeCompare(b.code)
          : b.code.localeCompare(a.code)
      );
  };

  const productCodes = filterAndSortCodes(settings?.productCodes ?? []);
  const unitCodes = filterAndSortCodes(settings?.unitCodes ?? []);
  const processCodes = filterAndSortCodes(settings?.processVersions ?? []);

  return (
    <div className={embedded ? 'space-y-6' : 'max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6'}>
      {embedded ? (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Batch Title Settings</h2>
              <p className="mt-1 text-sm text-gray-600">
                Set the product, unit, and process codes used to generate deterministic batch titles.
              </p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Most teams only update this when a new SKU, unit, or process version is introduced.
            </div>
          </div>
        </section>
      ) : (
        <PageHeader
          title="Batch Title Settings"
          description="Manage codes used to generate deterministic batch titles."
          actions={
            <Button variant="outline" onClick={() => navigate('/settings')}>
              Back to Settings
            </Button>
          }
        />
      )}

        {error && (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        )}
        {actionError && (
          <Alert variant="error" className="mb-4" onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}
        {actionSuccess && (
          <Alert variant="success" className="mb-4" onClose={() => setActionSuccess(null)}>
            {actionSuccess}
          </Alert>
        )}

      <FilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by code, name, or unit type..."
        statusOptions={[
          { value: 'all', label: 'All', count: allCodes.length },
          { value: 'active', label: 'Active', count: activeCodes },
          { value: 'inactive', label: 'Inactive', count: inactiveCodes },
        ]}
        selectedStatus={statusFilter}
        onStatusChange={(value) => setStatusFilter(value as 'all' | 'active' | 'inactive')}
        sortOptions={[
          { value: 'code-asc', label: 'Sort: Code A-Z' },
          { value: 'code-desc', label: 'Sort: Code Z-A' },
        ]}
        selectedSort={sortBy}
        onSortChange={(value) => setSortBy(value as 'code-asc' | 'code-desc')}
      />

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm text-gray-600">Loading settings...</div>
      ) : (
        <>
          <section className="rounded-xl border border-gray-200 bg-white shadow-sm p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Product Codes</h2>
                  <p className="text-sm text-gray-600">
                    Codes must be 2-6 uppercase letters or numbers. Codes are immutable once created.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <Input
                  label="Code"
                  value={newProductCode}
                  onChange={(e) => setNewProductCode(e.target.value.toUpperCase())}
                  placeholder="STJ"
                />
                <Input
                  label="Name"
                  value={newProductName}
                  onChange={(e) => setNewProductName(e.target.value)}
                  placeholder="Strawberry Jam"
                />
                <div className="flex items-end">
                  <Button
                    onClick={() => handleCreate('product')}
                    isLoading={creatingType === 'product'}
                  >
                    Add Product Code
                  </Button>
                </div>
              </div>

              <div className="mt-6 overflow-x-auto">
                <table className="min-w-full text-sm" aria-label="Product codes">
                  <thead className="text-left text-gray-500">
                    <tr>
                      <th scope="col" className="py-2 pr-4 font-medium">Code</th>
                      <th scope="col" className="py-2 pr-4 font-medium">Name</th>
                      <th scope="col" className="py-2 pr-4 font-medium">Active</th>
                      <th scope="col" className="py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    {productCodes.map((item) => (
                      <tr key={item.id} className="border-t border-gray-100">
                        <td className="py-3 pr-4 font-mono text-gray-900">{item.code}</td>
                        <td className="py-3 pr-4">
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded-md"
                            value={item.name ?? ''}
                            onChange={(e) => updateLocal('product', item.id, { name: e.target.value })}
                          />
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={item.isActive}
                              onChange={(e) => updateLocal('product', item.id, { isActive: e.target.checked })}
                              aria-label={`Set product code ${item.code} active state`}
                            />
                            <StatusBadge tone={item.isActive ? 'active' : 'disabled'} size="sm">
                              {item.isActive ? 'Active' : 'Inactive'}
                            </StatusBadge>
                          </div>
                        </td>
                        <td className="py-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSave('product', item)}
                            isLoading={savingId === item.id}
                          >
                            Save
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {productCodes.length === 0 && (
                      <tr>
                        <td colSpan={4} className="py-4 text-gray-500">
                          No product codes yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white shadow-sm p-6">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">Unit Codes</h2>
                  <button
                    type="button"
                    title={unitTypeHelpText}
                    aria-label="Explain unit type"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full text-blue-600 hover:bg-blue-50"
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  Use unit codes for farms, kitchens, or production lines. Codes are immutable once created.
                </p>
                <p className="mt-2 text-sm text-blue-700">
                  <span className="font-medium">Unit Type</span> tells users what kind of unit this is.
                  Example: <span className="font-medium">kitchen</span>, <span className="font-medium">farm</span>, <span className="font-medium">line</span>, or <span className="font-medium">warehouse</span>.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <Input
                  label="Code"
                  value={newUnitCode}
                  onChange={(e) => setNewUnitCode(e.target.value.toUpperCase())}
                  placeholder="K1"
                />
                <Input
                  label="Name"
                  value={newUnitName}
                  onChange={(e) => setNewUnitName(e.target.value)}
                  placeholder="Kitchen 1"
                />
                <Input
                  label="Unit Type"
                  value={newUnitType}
                  onChange={(e) => setNewUnitType(e.target.value)}
                  placeholder="kitchen"
                  helperText="Example: kitchen, farm, line, plant, or warehouse."
                />
                <div className="flex items-end">
                  <Button
                    onClick={() => handleCreate('unit')}
                    isLoading={creatingType === 'unit'}
                  >
                    Add Unit Code
                  </Button>
                </div>
              </div>

              <div className="mt-6 overflow-x-auto">
                <table className="min-w-full text-sm" aria-label="Unit codes">
                  <thead className="text-left text-gray-500">
                    <tr>
                      <th scope="col" className="py-2 pr-4 font-medium">Code</th>
                      <th scope="col" className="py-2 pr-4 font-medium">Name</th>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        <span className="inline-flex items-center gap-1">
                          <span>Unit Type</span>
                          <button
                            type="button"
                            title={unitTypeHelpText}
                            aria-label="Explain unit type in table"
                            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-blue-600 hover:bg-blue-50"
                          >
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      </th>
                      <th scope="col" className="py-2 pr-4 font-medium">Active</th>
                      <th scope="col" className="py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    {unitCodes.map((item) => (
                      <tr key={item.id} className="border-t border-gray-100">
                        <td className="py-3 pr-4 font-mono text-gray-900">{item.code}</td>
                        <td className="py-3 pr-4">
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded-md"
                            value={item.name ?? ''}
                            onChange={(e) => updateLocal('unit', item.id, { name: e.target.value })}
                          />
                        </td>
                        <td className="py-3 pr-4">
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded-md"
                            value={item.unitType ?? ''}
                            onChange={(e) => updateLocal('unit', item.id, { unitType: e.target.value })}
                            placeholder="e.g., kitchen, farm, line"
                            title={unitTypeHelpText}
                          />
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={item.isActive}
                              onChange={(e) => updateLocal('unit', item.id, { isActive: e.target.checked })}
                              aria-label={`Set unit code ${item.code} active state`}
                            />
                            <StatusBadge tone={item.isActive ? 'active' : 'disabled'} size="sm">
                              {item.isActive ? 'Active' : 'Inactive'}
                            </StatusBadge>
                          </div>
                        </td>
                        <td className="py-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSave('unit', item)}
                            isLoading={savingId === item.id}
                          >
                            Save
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {unitCodes.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-4 text-gray-500">
                          No unit codes yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white shadow-sm p-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Process Versions</h2>
                <p className="text-sm text-gray-600">
                  Optional codes for recipes or process versions.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <Input
                  label="Code"
                  value={newProcessCode}
                  onChange={(e) => setNewProcessCode(e.target.value.toUpperCase())}
                  placeholder="F1"
                />
                <Input
                  label="Name"
                  value={newProcessName}
                  onChange={(e) => setNewProcessName(e.target.value)}
                  placeholder="Formula 1"
                />
                <div className="flex items-end">
                  <Button
                    onClick={() => handleCreate('process')}
                    isLoading={creatingType === 'process'}
                  >
                    Add Process Version
                  </Button>
                </div>
              </div>

              <div className="mt-6 overflow-x-auto">
                <table className="min-w-full text-sm" aria-label="Process version codes">
                  <thead className="text-left text-gray-500">
                    <tr>
                      <th scope="col" className="py-2 pr-4 font-medium">Code</th>
                      <th scope="col" className="py-2 pr-4 font-medium">Name</th>
                      <th scope="col" className="py-2 pr-4 font-medium">Active</th>
                      <th scope="col" className="py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    {processCodes.map((item) => (
                      <tr key={item.id} className="border-t border-gray-100">
                        <td className="py-3 pr-4 font-mono text-gray-900">{item.code}</td>
                        <td className="py-3 pr-4">
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded-md"
                            value={item.name ?? ''}
                            onChange={(e) => updateLocal('process', item.id, { name: e.target.value })}
                          />
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={item.isActive}
                              onChange={(e) => updateLocal('process', item.id, { isActive: e.target.checked })}
                              aria-label={`Set process code ${item.code} active state`}
                            />
                            <StatusBadge tone={item.isActive ? 'active' : 'disabled'} size="sm">
                              {item.isActive ? 'Active' : 'Inactive'}
                            </StatusBadge>
                          </div>
                        </td>
                        <td className="py-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSave('process', item)}
                            isLoading={savingId === item.id}
                          >
                            Save
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {processCodes.length === 0 && (
                      <tr>
                        <td colSpan={4} className="py-4 text-gray-500">
                          No process versions yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
          </section>
        </>
      )}
    </div>
  );
}

export default function BatchTitleSettingsPage() {
  return <Navigate to="/products/create-batch#batch-title-settings-section" replace />;
}
